import React, { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Play, RefreshCw, AlertTriangle, FilePlus } from "lucide-react";

function fmtMoney(cents) {
    if (cents == null) return "—";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Phase RECURR-RESUME-CATCHUP (2026-07-28) + Phase RECURR-RESUME-CATCHUP-EXT
 * (2026-07-28) — Schedule catch-up modal.
 *
 * Two modes:
 *   - ``mode="resume"`` (default) — opened from the schedule card's
 *     Resume button on a paused schedule. Offers "Resume without
 *     issuing" + "Resume + Issue selected drafts". Hits
 *     ``POST /admin/billing-schedules/{id}/resume`` with an optional
 *     ``issue_missed`` body.
 *   - ``mode="edit"`` — opened from the post-edit banner on an ACTIVE
 *     schedule when missed occurrences are detected after saving edits.
 *     Offers "Close" + "Issue selected drafts" only (no resume — the
 *     schedule is already active). Hits
 *     ``POST /admin/billing-schedules/{id}/backfill-missed``.
 *
 * Backfilled invoices always land as ``status="draft"`` — admin still
 * has to review & send them. No auto-send in either mode.
 */
export function ScheduleCatchupModal({
    open,
    onOpenChange,
    schedule,
    mode = "resume",
    onResumed,
    onBackfilled,
}) {
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [missed, setMissed] = useState([]);
    const [meta, setMeta] = useState({});
    const [checked, setChecked] = useState({});

    const isEdit = mode === "edit";

    useEffect(() => {
        if (!open || !schedule?.id) return;
        setLoading(true);
        setChecked({});
        const ctx = isEdit ? "edit" : "resume";
        api.get(`/admin/billing-schedules/${schedule.id}/missed-occurrences?context=${ctx}`)
            .then(({ data }) => {
                setMissed(data.missed_occurrences || []);
                setMeta(data);
                const init = {};
                for (const m of data.missed_occurrences || []) init[m.scheduled_for] = true;
                setChecked(init);
            })
            .catch((e) => { toast.error(formatApiError(e)); setMissed([]); })
            .finally(() => setLoading(false));
    }, [open, schedule?.id, isEdit]);

    const selectedDates = () =>
        missed.filter((m) => checked[m.scheduled_for]).map((m) => m.scheduled_for);
    const selectedCount = selectedDates().length;

    // Resume flow — with or without backfill (mode="resume" only).
    const doResume = async (withBackfill) => {
        setBusy(true);
        try {
            const body = withBackfill ? { issue_missed: selectedDates() } : {};
            const { data } = await api.post(
                `/admin/billing-schedules/${schedule.id}/resume`, body,
            );
            const created = (data.backfilled_invoices || []).length;
            toast.success(created
                ? `${created} draft invoice${created === 1 ? "" : "s"} created; schedule resumed.`
                : "Schedule resumed.");
            if (onResumed) onResumed(data);
            onOpenChange(false);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    // Post-edit flow — backfill only (mode="edit").
    const doBackfill = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(
                `/admin/billing-schedules/${schedule.id}/backfill-missed`,
                { issue_missed: selectedDates() },
            );
            const created = (data.backfilled_invoices || []).length;
            toast.success(created
                ? `${created} draft invoice${created === 1 ? "" : "s"} created.`
                : "No new drafts created.");
            if (onBackfilled) onBackfilled(data);
            onOpenChange(false);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const title = isEdit ? "Missed occurrences detected" : "Resume schedule";
    const IconLead = isEdit ? FilePlus : Play;
    const editDescription =
        "After your edit, some past occurrences don't have invoices yet. " +
        "Review and backfill drafts as needed.";
    const resumeDescription = loading
        ? "Checking for missed invoices…"
        : (missed.length === 0
            ? "No invoices were missed during the pause. Ready to resume normal billing."
            : `This schedule was paused${meta.pause_started_at ? ` on ${meta.pause_started_at.slice(0,10)}` : ""}. Since then, ${missed.length} invoice${missed.length === 1 ? "" : "s"} would have been generated.`);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-2xl"
                data-testid={isEdit ? "schedule-catchup-modal-edit" : "resume-schedule-modal"}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <IconLead size={16} className="text-blue-900" />
                        {title}
                    </DialogTitle>
                    <DialogDescription>
                        {isEdit ? editDescription : resumeDescription}
                    </DialogDescription>
                </DialogHeader>

                {loading && (
                    <p className="py-6 text-sm text-slate-500 inline-flex items-center gap-2" data-testid="resume-schedule-loading">
                        <RefreshCw size={14} className="animate-spin" /> Loading…
                    </p>
                )}

                {!loading && missed.length > 0 && (
                    <div className="rounded-md border border-slate-200 overflow-hidden" data-testid="resume-schedule-missed-table">
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 text-left w-10"></th>
                                    <th className="px-3 py-2 text-left">Date</th>
                                    <th className="px-3 py-2 text-left">Description</th>
                                    <th className="px-3 py-2 text-left">Reason</th>
                                    <th className="px-3 py-2 text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {missed.map((m) => (
                                    <tr key={m.scheduled_for} data-testid={`resume-missed-row-${m.scheduled_for}`}>
                                        <td className="px-3 py-2">
                                            <input
                                                type="checkbox"
                                                checked={!!checked[m.scheduled_for]}
                                                onChange={(e) => setChecked((c) => ({ ...c, [m.scheduled_for]: e.target.checked }))}
                                                data-testid={`resume-missed-check-${m.scheduled_for}`}
                                                className="h-4 w-4"
                                            />
                                        </td>
                                        <td className="px-3 py-2 font-mono text-xs text-slate-700 tabular-nums">{m.scheduled_for}</td>
                                        <td className="px-3 py-2 text-slate-800 truncate max-w-[240px]" title={m.description}>{m.description}</td>
                                        <td className="px-3 py-2 text-[11px]">
                                            <span
                                                data-testid={`resume-missed-reason-${m.scheduled_for}`}
                                                className={
                                                    m.reason === "pause_window"
                                                        ? "inline-flex items-center rounded-full px-2 py-0.5 font-medium bg-amber-50 text-amber-900 ring-1 ring-inset ring-amber-200"
                                                        : "inline-flex items-center rounded-full px-2 py-0.5 font-medium bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200"
                                                }
                                            >
                                                {m.reason === "pause_window" ? "Pause" : "Gap"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtMoney(m.amount_cents)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && missed.length > 0 && (
                    <p className="text-[11px] text-slate-500 flex items-center gap-1.5" data-testid="resume-schedule-help">
                        <AlertTriangle size={11} className="text-amber-600" />
                        Backfilled invoices land as <span className="font-medium">draft</span> — you still need to review &amp; send each one.
                    </p>
                )}

                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={busy}
                        data-testid="resume-schedule-cancel"
                    >
                        {isEdit ? "Close" : "Cancel"}
                    </Button>

                    {/* Resume mode: 0-missed → single Resume; N>0 → both actions. */}
                    {!isEdit && !loading && missed.length === 0 && (
                        <Button
                            onClick={() => doResume(false)}
                            disabled={busy}
                            className="bg-blue-900 hover:bg-blue-800"
                            data-testid="resume-schedule-confirm"
                        >
                            Resume
                        </Button>
                    )}
                    {!isEdit && !loading && missed.length > 0 && (
                        <>
                            <Button
                                variant="outline"
                                onClick={() => doResume(false)}
                                disabled={busy}
                                data-testid="resume-schedule-no-backfill"
                            >
                                Resume without issuing
                            </Button>
                            <Button
                                onClick={() => doResume(true)}
                                disabled={busy || selectedCount === 0}
                                className="bg-blue-900 hover:bg-blue-800"
                                data-testid="resume-schedule-with-backfill"
                            >
                                Resume + Issue {selectedCount} draft{selectedCount === 1 ? "" : "s"}
                            </Button>
                        </>
                    )}

                    {/* Edit mode: backfill-only. Never offers Resume. */}
                    {isEdit && !loading && (
                        <Button
                            onClick={doBackfill}
                            disabled={busy || selectedCount === 0}
                            className="bg-blue-900 hover:bg-blue-800"
                            data-testid="schedule-catchup-issue-drafts"
                        >
                            Issue {selectedCount} draft{selectedCount === 1 ? "" : "s"}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Backwards-compat: existing imports reference ResumeScheduleModal.
export default ScheduleCatchupModal;
